// WebGL2 base for every stimulus: context creation (float16 drawing buffer when Chrome
// supports it), device-pixel sizing, program compilation, pixel readback.
//
// A stimulus is a fragment shader drawn over a full-screen triangle (see Stimulus below).
// visualTools' GLSL 330 shaders port to GLSL ES 3.00 with small changes; see
// docs/PORTING_STIMULI.md.

export interface StageInfo {
  webgl2: boolean;
  float16: boolean;
  drawingBufferFormat: string;
  /** Bits the drawing buffer can hold; whether they reach the panel is the step test's job. */
  bufferBits: number;
  colorSpace: string;
  renderer: string;
}

export interface DrawContext {
  /** Bits the station's output carries (8 or 10); sets the dither LSB. */
  outputBits: number;
  /** Increments every frame drawn; for temporal effects such as dither. */
  frameIndex: number;
}

/** A stimulus draws one frame onto a GLStage from its own parameters. */
export interface Stimulus<P> {
  readonly name: string;
  draw(stage: GLStage, params: P, ctx: DrawContext): void;
}

export const FULLSCREEN_VS = `#version 300 es
void main() {
  vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

/** Shared GLSL helpers available to every stimulus (prepend after the #version line). */
export const GLSL_COMMON = `
float vtHash(vec2 p) {
  p = fract(p * vec2(0.1031, 0.1030));
  p += dot(p, p.yx + 33.33);
  return fract((p.x + p.y) * p.x);
}
// Add +/- amplitude/2 output LSBs of noise; amplitude 0 disables.
float vtDither(float c, vec2 px, float frame, float amplitude, float lsb) {
  if (amplitude <= 0.0) return c;
  return c + (vtHash(px + frame * 17.0) - 0.5) * amplitude * lsb;
}
`;

type GL2 = WebGL2RenderingContext & {
  drawingBufferStorage?: (format: number, width: number, height: number) => void;
  drawingBufferFormat?: number;
  drawingBufferColorSpace?: string;
};

export class GLStage {
  readonly gl: GL2;
  readonly info: StageInfo;
  private frame = 0;
  private readonly programs = new Map<string, WebGLProgram>();

  constructor(readonly canvas: HTMLCanvasElement, opts: { preferFloat16?: boolean } = {}) {
    const gl = canvas.getContext("webgl2", {
      alpha: true, antialias: false, depth: false, stencil: false,
      premultipliedAlpha: true, preserveDrawingBuffer: true,
      powerPreference: "high-performance",
    }) as GL2 | null;
    if (!gl) throw new Error("WebGL2 is not available in this browser");
    this.gl = gl;
    try {
      gl.drawingBufferColorSpace = "srgb";
    } catch {
      /* srgb is the default */
    }
    let float16 = false;
    if (opts.preferFloat16 !== false && typeof gl.drawingBufferStorage === "function") {
      gl.getExtension("EXT_color_buffer_half_float");
      gl.getExtension("EXT_color_buffer_float");
      float16 = this.allocate(canvas.width || 1, canvas.height || 1);
    }
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    this.info = {
      webgl2: true,
      float16,
      drawingBufferFormat: float16 ? "RGBA16F" : "RGBA8",
      bufferBits: float16 ? 16 : 8,
      colorSpace: gl.drawingBufferColorSpace ?? "srgb",
      renderer: dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : "",
    };
  }

  get width(): number { return this.gl.drawingBufferWidth; }
  get height(): number { return this.gl.drawingBufferHeight; }

  /** Default output depth for this buffer when no calibration says otherwise. */
  get defaultOutputBits(): number { return this.info.float16 ? 10 : 8; }

  nextFrameIndex(): number {
    this.frame = (this.frame + 1) % 4096;
    return this.frame;
  }

  private allocate(width: number, height: number): boolean {
    const gl = this.gl;
    if (typeof gl.drawingBufferStorage !== "function") return false;
    while (gl.getError() !== gl.NO_ERROR) { /* clear */ }
    try {
      gl.drawingBufferStorage(gl.RGBA16F, width, height);
    } catch {
      return false;
    }
    return gl.getError() === gl.NO_ERROR && gl.drawingBufferFormat === gl.RGBA16F;
  }

  /** Size the drawing buffer in device pixels. */
  resize(widthPx: number, heightPx: number): void {
    if (this.canvas.width !== widthPx || this.canvas.height !== heightPx) {
      this.canvas.width = widthPx;
      this.canvas.height = heightPx;
    }
    const gl = this.gl;
    if (this.info?.float16 && (gl.drawingBufferWidth !== widthPx || gl.drawingBufferHeight !== heightPx
        || gl.drawingBufferFormat !== gl.RGBA16F)) {
      this.allocate(widthPx, heightPx);
    }
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
  }

  /** Compile (once) a fragment shader drawn over the full-screen triangle. */
  program(key: string, fragmentSource: string): WebGLProgram {
    const cached = this.programs.get(key);
    if (cached) return cached;
    const p = link(this.gl, FULLSCREEN_VS, fragmentSource);
    this.programs.set(key, p);
    return p;
  }

  drawFullscreen(program: WebGLProgram): void {
    this.gl.useProgram(program);
    this.gl.drawArrays(this.gl.TRIANGLES, 0, 3);
  }

  /** Read one pixel back as floats 0..1 (tests and self-checks). */
  readPixel(x: number, y: number): [number, number, number, number] {
    const gl = this.gl;
    const fmt = gl.getParameter(gl.IMPLEMENTATION_COLOR_READ_FORMAT) as number;
    const type = gl.getParameter(gl.IMPLEMENTATION_COLOR_READ_TYPE) as number;
    if (type === gl.FLOAT) {
      const out = new Float32Array(4);
      gl.readPixels(x, y, 1, 1, fmt === gl.RGBA ? gl.RGBA : fmt, gl.FLOAT, out);
      return [out[0]!, out[1]!, out[2]!, out[3]!];
    }
    if (type === gl.HALF_FLOAT) {
      const out = new Uint16Array(4);
      gl.readPixels(x, y, 1, 1, gl.RGBA, gl.HALF_FLOAT, out);
      return [half(out[0]!), half(out[1]!), half(out[2]!), half(out[3]!)];
    }
    const out = new Uint8Array(4);
    gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, out);
    return [out[0]! / 255, out[1]! / 255, out[2]! / 255, out[3]! / 255];
  }
}

function half(h: number): number {
  const s = (h & 0x8000) ? -1 : 1;
  const e = (h >> 10) & 0x1f;
  const f = h & 0x3ff;
  if (e === 0) return s * 2 ** -14 * (f / 1024);
  if (e === 31) return f ? NaN : s * Infinity;
  return s * 2 ** (e - 15) * (1 + f / 1024);
}

export function link(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const compile = (type: number, src: string) => {
    const sh = gl.createShader(type)!;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      throw new Error(`shader compile failed: ${gl.getShaderInfoLog(sh)}`);
    }
    return sh;
  };
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compile(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    throw new Error(`program link failed: ${gl.getProgramInfoLog(p)}`);
  }
  return p;
}

/** Cache uniform locations for a program. */
export function uniforms(gl: WebGL2RenderingContext, p: WebGLProgram, names: string[]): Record<string, WebGLUniformLocation | null> {
  const out: Record<string, WebGLUniformLocation | null> = {};
  for (const n of names) out[n] = gl.getUniformLocation(p, n);
  return out;
}
